import React, { useState, useEffect, useRef } from 'react';
import { metaApi, reportsApi } from '../services/api';
import toast from 'react-hot-toast';
import html2canvas from 'html2canvas';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';

const PageReports = () => {
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState('');
  const [datePreset, setDatePreset] = useState('last_30d');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [shareUrl, setShareUrl] = useState('');
  const [shareCopied, setShareCopied] = useState(false);
  const [exporting, setExporting] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const res = await metaApi.getPages();
      const availablePages = res.data?.pages || [];
      setPages(availablePages);
      if (availablePages.length > 0) {
        setSelectedPage(availablePages[0].id);
      }
    } catch (error) {
      toast.error('Erro ao buscar páginas');
    }
  };

  const generateReport = async () => {
    if (!selectedPage) {
      toast.error('Selecione uma página primeiro');
      return;
    }

    setLoading(true);
    setReportData(null);

    try {
      const response = await metaApi.getPageInsights(selectedPage, {
        datePreset,
        period: 'day'
      });
      
      if (response.success) {
        processReportData(response.data);
      }
    } catch (error) {
      toast.error('Erro ao gerar relatório de página');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (!reportData || !selectedPage) return;
    try {
      const pageName = pages.find(p => p.id === selectedPage)?.name || selectedPage;
      const resp = await reportsApi.shareReport({
        reportType: 'page',
        entityId: selectedPage,
        entityName: `Relatório de Página: ${pageName}`,
        options: { reportData, datePreset, pageId: selectedPage, pageName },
      });
      const url = resp.data?.url || '';
      setShareUrl(url);
      navigator.clipboard.writeText(url).catch(() => {});
      toast.success('Link copiado para a área de transferência!');
    } catch {
      toast.error('Erro ao gerar link de compartilhamento');
    }
  };

  const handleCopyShare = () => {
    navigator.clipboard.writeText(shareUrl);
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2000);
  };

  const handleExportPng = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const el = reportRef.current;
      const orig = { width: el.style.width, maxWidth: el.style.maxWidth };
      el.style.width = '1920px';
      el.style.maxWidth = '1920px';
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, windowWidth: 1920 });
      el.style.width = orig.width;
      el.style.maxWidth = orig.maxWidth;
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `relatorio_pagina_${new Date().toISOString().split('T')[0]}.png`;
      link.click();
    } catch (err) {
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  const processReportData = (data) => {
    const { insights, fan_count } = data;
    
    // Process insights into chart data and totals
    const metricsMap = {};
    const chartData = {};

    insights.forEach(metric => {
      metricsMap[metric.name] = metric.values;
      
      metric.values.forEach(val => {
        const dateStr = new Date(val.end_time).toLocaleDateString();
        if (!chartData[dateStr]) {
          chartData[dateStr] = { date: dateStr };
        }
        chartData[dateStr][metric.name] = val.value;
      });
    });

    // Calculate totals
    const totals = {
      impressions: 0,
      reach: 0,
      engagement: 0,
      views: 0
    };

    if (metricsMap['page_impressions']) {
      totals.impressions = metricsMap['page_impressions'].reduce((sum, item) => sum + item.value, 0);
    }
    if (metricsMap['page_impressions_unique']) {
      totals.reach = metricsMap['page_impressions_unique'].reduce((sum, item) => sum + item.value, 0);
    }
    if (metricsMap['page_post_engagements']) {
      totals.engagement = metricsMap['page_post_engagements'].reduce((sum, item) => sum + item.value, 0);
    }
    if (metricsMap['page_views_total']) {
      totals.views = metricsMap['page_views_total'].reduce((sum, item) => sum + item.value, 0);
    }

    setReportData({
      chartData: Object.values(chartData),
      totals,
      fanCount: fan_count
    });
  };

  return (
    <div className="animate-fade-in pb-16">
      <div className="mb-8">
        <h1 className="font-headline font-extrabold text-3xl tracking-tight text-on-surface mb-2">
          Relatórios de Página
        </h1>
        <p className="text-secondary font-body">
          Analise o desempenho da sua página no Facebook e Instagram
        </p>
      </div>

      {/* Controls */}
      <div className="bg-surface-container-lowest rounded-xl p-6 shadow-[0_8px_32px_rgba(25,28,30,0.04)] mb-8 flex flex-col md:flex-row gap-4 items-end">
        <div className="flex-1 w-full relative">
          <label className="block text-xs font-bold font-label uppercase tracking-wider text-secondary mb-2">
            Página
          </label>
          <div className="relative">
            <select
              className="input w-full appearance-none pr-10 bg-surface text-on-surface font-medium border-outline-variant/50 focus:border-primary shadow-sm"
              value={selectedPage}
              onChange={(e) => setSelectedPage(e.target.value)}
            >
              <option value="" disabled>Selecione uma página</option>
              {pages.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-secondary text-xl">
              arrow_drop_down
            </span>
          </div>
        </div>

        <div className="w-full md:w-64 relative">
          <label className="block text-xs font-bold font-label uppercase tracking-wider text-secondary mb-2">
            Período
          </label>
          <div className="relative">
            <select
              className="input w-full appearance-none pr-10 bg-surface text-on-surface font-medium border-outline-variant/50 focus:border-primary shadow-sm"
              value={datePreset}
              onChange={(e) => setDatePreset(e.target.value)}
            >
              <option value="last_7d">Últimos 7 dias</option>
              <option value="last_14d">Últimos 14 dias</option>
              <option value="last_30d">Últimos 30 dias</option>
              <option value="last_90d">Últimos 90 dias</option>
            </select>
            <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-secondary text-xl">
              history
            </span>
          </div>
        </div>

        <button
          onClick={generateReport}
          disabled={loading || !selectedPage}
          className="w-full md:w-auto primary-pill-gradient text-white px-8 py-3 rounded-xl font-bold font-headline hover:opacity-90 transition-all flex items-center justify-center gap-2 shadow-sm disabled:opacity-50"
        >
          {loading ? (
            <span className="material-symbols-outlined animate-spin">refresh</span>
          ) : (
            <span className="material-symbols-outlined">analytics</span>
          )}
          {loading ? 'Gerando...' : 'Gerar Relatório'}
        </button>
      </div>

      {/* Results */}
      {reportData && (
        <div className="space-y-8 animate-fade-in-up">
          {/* Actions */}
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={handleShare} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-surface-container hover:bg-surface-container-high transition-colors text-sm font-bold text-on-surface">
              <span className="material-symbols-outlined text-[18px]">share</span>
              Compartilhar Link
            </button>
            {shareUrl && (
              <div className="bg-green-50 border border-green-200 rounded-full px-4 py-2 flex items-center gap-2 max-w-[280px] overflow-hidden">
                <span className="material-symbols-outlined text-green-600 text-sm shrink-0">link</span>
                <span className="text-green-700 text-xs truncate">{shareUrl}</span>
                <button onClick={handleCopyShare} className="text-green-600 hover:text-green-800 text-xs font-bold whitespace-nowrap">
                  {shareCopied ? 'Copiado!' : 'Copiar'}
                </button>
              </div>
            )}
            <button onClick={handleExportPng} disabled={exporting} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors text-sm font-bold disabled:opacity-50">
              <span className="material-symbols-outlined text-[18px]">{exporting ? 'hourglass_empty' : 'image'}</span>
              {exporting ? 'Exportando...' : 'Exportar PNG'}
            </button>
          </div>
          <div ref={reportRef}>
          
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-surface-container-lowest p-5 rounded-2xl border border-outline-variant/30 flex flex-col justify-center items-center text-center shadow-sm">
              <span className="material-symbols-outlined text-primary mb-2 text-3xl">visibility</span>
              <p className="text-xs font-bold text-secondary uppercase tracking-wider mb-1">Impressões</p>
              <p className="font-headline font-extrabold text-2xl text-on-surface">{reportData.totals.impressions.toLocaleString('pt-BR')}</p>
            </div>
            <div className="bg-surface-container-lowest p-5 rounded-2xl border border-outline-variant/30 flex flex-col justify-center items-center text-center shadow-sm">
              <span className="material-symbols-outlined text-blue-500 mb-2 text-3xl">public</span>
              <p className="text-xs font-bold text-secondary uppercase tracking-wider mb-1">Alcance (Aprox)</p>
              <p className="font-headline font-extrabold text-2xl text-on-surface">{reportData.totals.reach.toLocaleString('pt-BR')}</p>
            </div>
            <div className="bg-surface-container-lowest p-5 rounded-2xl border border-outline-variant/30 flex flex-col justify-center items-center text-center shadow-sm">
              <span className="material-symbols-outlined text-purple-500 mb-2 text-3xl">touch_app</span>
              <p className="text-xs font-bold text-secondary uppercase tracking-wider mb-1">Engajamento</p>
              <p className="font-headline font-extrabold text-2xl text-on-surface">{reportData.totals.engagement.toLocaleString('pt-BR')}</p>
            </div>
            <div className="bg-surface-container-lowest p-5 rounded-2xl border border-outline-variant/30 flex flex-col justify-center items-center text-center shadow-sm">
              <span className="material-symbols-outlined text-amber-500 mb-2 text-3xl">pageview</span>
              <p className="text-xs font-bold text-secondary uppercase tracking-wider mb-1">Visualizações</p>
              <p className="font-headline font-extrabold text-2xl text-on-surface">{reportData.totals.views.toLocaleString('pt-BR')}</p>
            </div>
            <div className="bg-surface-container-lowest p-5 rounded-2xl border border-outline-variant/30 flex flex-col justify-center items-center text-center shadow-sm relative overflow-hidden">
              <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-primary/20 to-transparent rounded-bl-full"></div>
              <span className="material-symbols-outlined text-emerald-500 mb-2 text-3xl">groups</span>
              <p className="text-xs font-bold text-secondary uppercase tracking-wider mb-1">Seguidores Fãs</p>
              <p className="font-headline font-extrabold text-3xl text-primary">{reportData.fanCount.toLocaleString('pt-BR')}</p>
            </div>
          </div>

          {/* Chart */}
          <div className="bg-surface-container-lowest p-6 rounded-2xl border border-outline-variant/30 shadow-sm">
            <h3 className="font-headline font-bold text-lg mb-6">Evolução do Desempenho</h3>
            <div className="h-[400px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={reportData.chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} dx={-10} />
                  <RechartsTooltip 
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}
                  />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} />
                  <Line type="monotone" name="Impressões" dataKey="page_impressions" stroke="#4f46e5" strokeWidth={3} dot={false} activeDot={{ r: 6 }} />
                  <Line type="monotone" name="Alcance" dataKey="page_impressions_unique" stroke="#3b82f6" strokeWidth={3} dot={false} activeDot={{ r: 6 }} />
                  <Line type="monotone" name="Engajamento" dataKey="page_post_engagements" stroke="#a855f7" strokeWidth={3} dot={false} activeDot={{ r: 6 }} />
                  <Line type="monotone" name="Visualizações" dataKey="page_views_total" stroke="#f59e0b" strokeWidth={3} dot={false} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          </div>{/* end reportRef */}
        </div>
      )}

    </div>
  );
};

export default PageReports;
